import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import useLang from '../hooks/useLang';

const ACTION_COLORS = {
  login:              'bg-green-100 text-green-800',
  create_transaction: 'bg-blue-100 text-blue-800',
  delete_transaction: 'bg-red-100 text-red-800',
  create_member:      'bg-blue-100 text-blue-800',
  update_member:      'bg-yellow-100 text-yellow-800',
  delete_member:      'bg-red-100 text-red-800',
  create_expense:     'bg-purple-100 text-purple-800',
  delete_expense:     'bg-red-100 text-red-800',
  update_user:        'bg-yellow-100 text-yellow-800',
  delete_user:        'bg-red-100 text-red-800',
  update_permissions: 'bg-indigo-100 text-indigo-800',
};

const PAGE_SIZE = 50;

export default function AuditLog() {
  const { T } = useLang();
  const A = T.auditLog;

  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset]   = useState(0);
  const [search, setSearch]   = useState('');
  const [action, setAction]   = useState('');
  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');

  const load = useCallback((off = 0) => {
    setLoading(true);
    const params = new URLSearchParams({ limit: PAGE_SIZE, offset: off });
    if (search) params.set('search', search);
    if (action) params.set('action', action);
    if (from)   params.set('from', from);
    if (to)     params.set('to', to);
    api.get(`/audit-logs?${params}`)
      .then(r => { setRows(r.data.rows || []); setTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [search, action, from, to]);

  useEffect(() => { setOffset(0); load(0); }, [search, action, from, to, load]);

  const goPage = (newOffset) => { setOffset(newOffset); load(newOffset); };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  function fmt(dt) {
    if (!dt) return '—';
    return new Date(dt).toLocaleString('sw-TZ', { dateStyle: 'short', timeStyle: 'short' });
  }

  return (
    <div className="p-4 max-w-7xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">{A.title}</h1>
      <p className="text-sm text-gray-500 mb-4">{A.subtitle}</p>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap gap-3">
        <input
          type="text"
          placeholder={A.searchPlaceholder}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-[160px]"
        />
        <select
          value={action}
          onChange={e => setAction(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
        >
          <option value="">{A.allActions}</option>
          <option value="login">{A.actions.login}</option>
          <option value="create_transaction">{A.actions.create_transaction}</option>
          <option value="delete_transaction">{A.actions.delete_transaction}</option>
          <option value="create_member">{A.actions.create_member}</option>
          <option value="update_member">{A.actions.update_member}</option>
          <option value="delete_member">{A.actions.delete_member}</option>
          <option value="create_expense">{A.actions.create_expense}</option>
          <option value="delete_expense">{A.actions.delete_expense}</option>
          <option value="update_user">{A.actions.update_user}</option>
          <option value="delete_user">{A.actions.delete_user}</option>
        </select>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>{A.from}:</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span>{A.to}:</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </div>
        {(search || action || from || to) && (
          <button onClick={() => { setSearch(''); setAction(''); setFrom(''); setTo(''); }}
            className="text-sm text-red-600 hover:underline">
            {A.clearFilters}
          </button>
        )}
      </div>

      {/* Stats bar */}
      <div className="text-sm text-gray-500 mb-2">
        {A.totalRecords}: <span className="font-semibold text-gray-800">{total}</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400">{A.loading}</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-12 text-gray-400">{A.noData}</div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{A.col.time}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{A.col.user}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{A.col.role}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{A.col.action}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700">{A.col.description}</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-700 whitespace-nowrap">{A.col.ip}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(row => (
                  <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 whitespace-nowrap text-gray-500 text-xs">{fmt(row.created_at)}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{row.user_name || '—'}</div>
                      <div className="text-xs text-gray-400">{row.user_phone || ''}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-xs bg-gray-100 text-gray-600 rounded px-2 py-0.5">{row.user_role || '—'}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`text-xs rounded-full px-2 py-0.5 font-medium ${ACTION_COLORS[row.action] || 'bg-gray-100 text-gray-600'}`}>
                        {A.actions[row.action] || row.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-700 max-w-xs">{row.description || '—'}</td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">{row.ip_address || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <button
            disabled={currentPage === 1}
            onClick={() => goPage(offset - PAGE_SIZE)}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
          >
            ← {A.prev}
          </button>
          <span className="text-sm text-gray-600">
            {A.page} {currentPage} / {totalPages}
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() => goPage(offset + PAGE_SIZE)}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
          >
            {A.next} →
          </button>
        </div>
      )}
    </div>
  );
}
